<script lang="ts" setup>
import dayjs from 'dayjs'
import { useGoodsStore } from '~/store/store-goods'
import { useUserStore } from '~/store/user'
// import { useRootAppStore } from '~/store/app'
import { useDisplayInfo } from '~/composable/common/goods/use-display-info'
// import { useTimerOld } from '~/composable/utils'
import { commaizeNumber } from '@/utils/primitive/number'
import { useDelivery } from '~/pages/goods/composables/ui/use-delivery'
import { GoodsDetailResponse, HeadCopyListItem } from '~/api/types/goods'
import ModalReturn from './layer/modal-exchange-return.vue'
import ModalDelivery from './layer/modal-dawn-delivery.vue'
import { useTooltip, TOOLTIP_TYPE } from '~/pages/goods/composables/ui'
import { useTimerFormat } from '~/composable/utils/use-timer-format'
import { useClockStore } from '~/store/common/clock'

interface Reactive {
  isShowReturn: boolean
  isShowDelivery: boolean
}

export interface Props {
  items: GoodsDetailResponse
  headCopy?: Array<HeadCopyListItem>
  bundle: boolean
}

const props = withDefaults(defineProps<Props>(), {})
const userStore = useUserStore()
const { custBasDlvrInfo } = storeToRefs(userStore)

const goodsStore = useGoodsStore()
const { isBasicErmrnDlvrPossYn } = storeToRefs(goodsStore)

const { shippingDate, deliveryPrice } = useDelivery()
// const rootAppStore = useRootAppStore()
// const { currentTime } = storeToRefs(rootAppStore)

const clockStore = useClockStore()

const { setTooltips, checkIsOpen, changeTooltips } = useTooltip()
setTooltips({ uuid: TOOLTIP_TYPE.DELIVERY1, isOpen: false })

const isTooltipOpen1 = computed(() => checkIsOpen(TOOLTIP_TYPE.DELIVERY1))

// const { state: timerState, startTimer, clearTimer } = useTimerOld()

const remainTime = computed<string>(() => {
  // if (timerState.min <= 0 && timerState.sec <= 0) {
  //   return ''
  // }
  //
  // const hour = Math.floor((timerState.min % (60 * 24)) / 60)
  //   .toString()
  //   .padStart(2, '0')
  // const minute = (timerState.min % 60).toString().padStart(2, '0')
  // const second = timerState.secView
  //
  // return `${hour}시간 ${minute}분 ${second}초`
  if (!isSingSingDlvrGoods.value || isBasicJejuMntnYn.value) return ''

  const { now } = storeToRefs(clockStore)
  const { secondsToTimeFormatKo } = useTimerFormat()
  const end = dayjs(now.value, 'YYYY-MM-DDTHH:mm:ss').set('h', 22).set('m', 0).set('s', 0)
  const remainingSeconds = clockStore.getRemainingSeconds(end)

  if (remainingSeconds < 0) {
    timerCallback()
    return ''
  }
  return secondsToTimeFormatKo(remainingSeconds)
})

const isTimeOver = ref<boolean>(false)
const timerCallback = () => {
  isTimeOver.value = true
}

const state = reactive<Reactive>({
  isShowReturn: false,
  isShowDelivery: false
})

const dispTypeCd = computed(() => props.items?.detailInfo?.dispTypeCd)
const { displayInfo } = useDisplayInfo(dispTypeCd.value)

const isLogin = computed(() => userStore.getUserState.custInfo?.loginId ?? false)

/** 기본 배송지 도서산간/제주 여부 */
const isBasicJejuMntnYn = computed<boolean>(() => custBasDlvrInfo.value?.isldMntnYn === 'Y')

/** 씽씽배송 상품 */
const isSingSingDlvrGoods = computed<boolean>(
  () =>
    props.items?.dlvrInfo?.qcDlvrYn === 'Y' &&
    intuitiveShippingDate.value !== '29990101' &&
    intuitiveShippingDate.value !== '29990102'
)
/** 예약배송 상품 */
const isScheduledDlvrGoods = computed<boolean>(() => props.items?.dlvrInfo?.rsrvDlvrGoodsYn === 'Y')

/** 상품 도서산간 배송 여부 Y: 가능, N: 불가, D:도서산간만, J:제주도만 */
const isJejuMntnDlvr = computed(() => props.items?.dlvrInfo?.rmaDlvrYn === 'Y')
const isOnlyJejuDlvr = computed(() => props.items?.dlvrInfo?.rmaDlvrYn === 'J')
const isOnlyMntnDlvr = computed(() => props.items?.dlvrInfo?.rmaDlvrYn === 'D')

/** 기본 배송지 도서산간 && 도서산간 배송불가 상품 */
const isNoJejuMntnDlvr = computed<boolean>(() => isBasicJejuMntnYn.value && !isJejuMntnDlvr.value)

/** 배송예정일 미노출 */
const isNoDlvrDate = computed<boolean>(() => !isBasicErmrnDlvrPossYn.value && isNoJejuMntnDlvr.value)

/** 묶음배송 여부 */
const isBundleDelivery = computed<boolean>(() => props.bundle)

/** 설치상품 */
const isInstGoods = computed<boolean>(() => props.items?.detailInfo?.instGoodsYn === 'Y')

/** 배송비 */
const deliveryPriceMsg = computed<string>(() => props.items?.dlvrInfo?.dlvrPrcMsg)

/** 예상 배송일 */
const intuitiveShippingDate = computed<string>(() => props.items?.dlvrInfo?.intuitiveShippingDate)

/** 센터배송 */
const isCenterDelivery = computed<boolean>(() => props.items?.dlvrInfo?.dlvrWayCd1 === '20')

/** 업체배송 */
const isPartnerDelivery = computed(() => {
  if (intuitiveShippingDate.value === '29990101' || intuitiveShippingDate.value === '29990102' || isInstGoods.value) {
    return true
  }
  if (!isSingSingDlvrGoods.value) {
    return true
  } else {
    if (
      isLogin.value &&
      !isBasicErmrnDlvrPossYn.value &&
      isBasicJejuMntnYn.value &&
      (isJejuMntnDlvr.value || isOnlyJejuDlvr.value || isOnlyMntnDlvr.value)
    ) {
      return true
    }
  }
})

/** 추가 배송정보 */
const additionalDlvrInfo = computed(() => {
  if (props.headCopy && props.headCopy.length > 0) {
    return props.headCopy?.[0].textCtnt
  }
})

/** 배송정보 */
const deliveryInfo = computed(() => {
  if (
    !isSingSingDlvrGoods.value ||
    (isLogin.value && !isBasicErmrnDlvrPossYn.value && isBasicJejuMntnYn.value && isJejuMntnDlvr.value)
  ) {
    return true
  }
})

/** 상품타입 */
const displayType = computed<boolean>(
  () => props.items?.detailInfo?.dispTypeCd === '10' || props.items?.detailInfo?.dispTypeCd === '15'
)

/** 툴팁 노출조건 */
const deliveryCondition = computed<boolean>(
  () =>
    intuitiveShippingDate.value === '29990101' ||
    intuitiveShippingDate.value === '29990102' ||
    isScheduledDlvrGoods.value ||
    isInstGoods.value
)

const additionalDlvrPrice = computed(() => {
  const dlvr = props.items?.dlvrInfo

  let returnMsg = ''

  if (isJejuMntnDlvr.value) {
    if (dlvr?.rmaJejuRegnDlvrExpns > 0 && dlvr?.rmaGnrlRegnDlvrExpns > 0) {
      returnMsg =
        `${'추가 배송비: 제주 '}${commaizeNumber(dlvr?.rmaJejuRegnDlvrExpns)}원, ` +
        `도서산간 ${commaizeNumber(dlvr?.rmaGnrlRegnDlvrExpns)}원`
    } else if (dlvr?.rmaJejuRegnDlvrExpns > 0 && dlvr?.rmaGnrlRegnDlvrExpns <= 0) {
      returnMsg = `${'추가 배송비: 제주 '}${commaizeNumber(dlvr?.rmaJejuRegnDlvrExpns)}원`
    } else if (dlvr?.rmaJejuRegnDlvrExpns <= 0 && dlvr?.rmaGnrlRegnDlvrExpns > 0) {
      returnMsg = `${'추가 배송비: 도서산간 '}${commaizeNumber(dlvr?.rmaGnrlRegnDlvrExpns)}원`
    }
  } else if (isOnlyMntnDlvr.value) {
    if (dlvr?.rmaGnrlRegnDlvrExpns > 0) {
      returnMsg = `${'추가 배송비: 도서산간 '}${commaizeNumber(dlvr?.rmaGnrlRegnDlvrExpns)}원, ` + '제주 배송 불가'
    } else {
      returnMsg = '제주 배송 불가'
    }
  } else if (isOnlyJejuDlvr.value) {
    if (dlvr?.rmaJejuRegnDlvrExpns > 0) {
      returnMsg = `${'추가 배송비: 제주 '}${commaizeNumber(dlvr?.rmaJejuRegnDlvrExpns)}원, ` + '도서산간 배송 불가'
    } else {
      returnMsg = '도서산간 배송 불가 '
    }
  } else {
    returnMsg = '제주·도서산간 배송 불가'
  }

  return returnMsg
})

// async function deliveryTimer() {
//   await rootAppStore.updateCurrentTimeAction()
//   const now = dayjs(currentTime.value)
//   const end = dayjs(currentTime.value, 'YYYY-MM-DDTHH:mm:ss').set('h', 22).set('m', 0).set('s', 0)
//
//   const diffSecond = end.diff(now, 'second') % 60
//   const diffMinute = end.diff(now, 'minute')
//
//   startTimer(diffMinute, diffSecond, timerCallback)
// }

onMounted(initDelivery)
onActivated(initDelivery)
// onDeactivated(clearDeliveryTimer)
// onUnmounted(clearDeliveryTimer)

async function initDelivery() {
  if (isLogin.value) {
    await userStore.resetUser()
  }
  if (isSingSingDlvrGoods.value && !isBasicJejuMntnYn.value) {
    // deliveryTimer()
  }
}

/**
 * icon-sing base 새벽,내일도착
 * icon-sing isolated 배송예정일 미노출
 * icon-delivery 업체배송
 */

function getDeliveryClass() {
  if (remainTime.value) {
    return 'icon-sing base'
  }
  if (isNoDlvrDate.value && isSingSingDlvrGoods.value) {
    return 'icon-sing isolated'
  }
  if (isPartnerDelivery.value) {
    return 'icon-delivery'
  }
}

// function clearDeliveryTimer() {
//   if (timerState.timer) {
//     delete timerState.callback
//     clearTimer(timerState.timer as number, 'end timer')
//   }
// }
</script>

<template>
  <div class="delivery-wrap">
    <div class="delivery-inner">
      <dl :class="getDeliveryClass()" v-if="!displayInfo.isFormless">
        <dt v-if="deliveryInfo">배송 정보</dt>
        <dt v-else><span class="blind">씽씽배송</span></dt>
        <dd>
          <div class="text-info" v-if="displayType && !isNoJejuMntnDlvr">
            <span class="tomorrow" v-if="!isNoDlvrDate">
              <span class="time" v-if="remainTime && !isTimeOver"
                ><strong>{{ remainTime }}</strong> 내 주문 시</span
              >
              {{ shippingDate(items, isBasicErmrnDlvrPossYn) }}
            </span>
            <div class="tooltip-wrap" v-if="!deliveryCondition">
              <button type="button" class="btn-tooltip" @click="changeTooltips(TOOLTIP_TYPE.DELIVERY1)">
                <span class="blind">안내</span>
              </button>
              <div class="tooltip-box" v-if="isTooltipOpen1">
                <ul class="bullet-list">
                  <li>최근 배송 정보를 분석한 예측된 내용을 제공합니다.</li>
                  <li>제주·도서산간 지역의 경우 도착 예정일이 변경될 수 있습니다.</li>
                  <li>판매자 또는 택배사의 사정으로 인해, 배송 정보의 변동이나 택배사의 변경이 있을 수 있습니다.</li>
                  <li v-if="!isCenterDelivery">
                    <span class="delivery-company">택배사: {{ items?.dlvrInfo?.outgoLocLsc }}</span>
                  </li>
                </ul>
                <button type="button" class="btn-close" @click="changeTooltips(TOOLTIP_TYPE.DELIVERY1, false)">
                  <span class="blind">안내 닫기</span>
                </button>
              </div>
            </div>
          </div>
          <p class="text">
            {{ deliveryPrice(items?.dlvrInfo?.dlvrPrc) }}
            <router-link
              :to="`/goods/bundle-delivery/${items?.detailInfo?.goodsCd}`"
              class="btn-link"
              v-if="isBundleDelivery"
              >묶음배송상품 보기</router-link
            >
          </p>
          <p class="etc-text">{{ deliveryPriceMsg }}</p>
          <p class="etc-text">
            {{ additionalDlvrPrice }}
          </p>
          <ModalDelivery v-model="state.isShowDelivery" />
          <button type="button" class="btn-sing" @click="state.isShowDelivery = true" v-if="!isPartnerDelivery">
            씽씽배송 안내
          </button>
        </dd>
      </dl>
      <div class="extra-delivery-wrap" v-if="additionalDlvrInfo">
        <details open>
          <summary>
            <strong class="title icon-reserves">추가 배송 정보</strong>
          </summary>
          <div class="toggle-box">
            <p class="text">
              {{ additionalDlvrInfo }}
            </p>
          </div>
        </details>
      </div>
    </div>
  </div>

  <div class="btn-return-wrap">
    <ModalReturn v-model="state.isShowReturn" />
    <button type="button" class="btn-return" @click="state.isShowReturn = true">배송·교환·반품 안내</button>
  </div>
</template>

<style lang="scss">
@use '~/pages/goods/scss/delivery.scss';
</style>
